import { Injectable } from '@angular/core';
import {
  Equipment,
  ExportVariables,
  GetRecurrenceQueryResponse,
  ListRecurrenceQueryResponse,
  Occurrence,
  PagedResult,
  Recurrence,
  RecurrencePageResult,
} from '@core/interfaces';
import { Apollo } from 'apollo-angular';
import { sortBy } from 'lodash';
import { RecurrenceQueries } from './recurrence.queries';

@Injectable({
  providedIn: 'root'
})
export class RecurrenceService {

  constructor(
    private readonly apollo: Apollo,
    private readonly queries: RecurrenceQueries
  ) { }

  async listRecurrences({ where, options }): Promise<RecurrencePageResult> {
    return this.apollo.query<ListRecurrenceQueryResponse>({
      query: this.queries.listRecurrences(),
      variables: {
        limit: options.limit,
        offset: options.offset,
        orderBy: options.orderBy,
        where: where
      }
    }).toPromise()
      .then((response) => {
        return {
          items: response.data.recurrences.items,
          totalCount: response.data.recurrences.totalCount,
          filtered: Boolean(
            where.archived !== false ||
            where.locationId_in ||
            where.segmentId_in ||
            where.interval_in ||
            where.maintenanceTypeId_in ||
            where.companyId_in
          )
        };
      });
  }

  async getById(id: string): Promise<Recurrence> {
    const query = this.queries.getRecurrenceById();
    const variables = { id };
    return this.apollo.query<GetRecurrenceQueryResponse>({
      query,
      variables
    }).toPromise()
      .then((response) => response.data.recurrence);
  }

  async save(id: string, recurrence: any): Promise<Recurrence> {
    if (id) {
      return this.update(id, recurrence);
    }

    return this.create(recurrence);
  }

  async create(recurrence: Recurrence): Promise<Recurrence> {
    return this.apollo.mutate<Recurrence>({
      mutation: this.queries.createRecurrence(),
      variables: {
        input: recurrence
      }
    }).toPromise()
    .then((result) => result.data['createRecurrence']);
  }

  async update(id: string, recurrence: Recurrence): Promise<Recurrence> {
    return this.apollo
      .mutate({
        mutation: this.queries.updateRecurrence(),
        variables: {
          id: id,
          input: recurrence
        }
      })
      .toPromise()
      .then((result) => result.data['updateRecurrence']);
  }

  async listOccurrences({ where, options }): Promise<PagedResult<Occurrence>> {
    return this.apollo
      .query({
        query: this.queries.listOccurrences(),
        variables: {
          limit: options.limit,
          offset: options.offset,
          orderBy: options.orderBy,
          where: where
        }
      })
      .toPromise()
      .then(({ data }) => data['occurrences']);
  }
}
